﻿import React from 'react';
import { MapContainer, TileLayer, ScaleControl, LayersControl, Polyline, Marker, Popup, useMap, LayerGroup } from 'react-leaflet';
import ImageOverlayRotated from './ImageOverlayRotated';
import icons from '../Icons/MarkerIcons';

import { useEventContext } from 'providers/EventProvider';
import { LatLngLiteral } from 'leaflet';
import ReactLeafletRightClick, { LeafletRightClickProvider } from './ReactLeafletRightClick';
import Players from '../Players/Players';
import HideAttributeControl from './HideAttributeControl';
import MapContextMenu from './MapContextMenu';
import RightDrawer from '../RightDrawer/RightDrawer';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';

type MapEventProps = {
  loading: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
};

const MapEvent = ({ loading, open, setOpen }: MapEventProps) => {
  const { event, options } = useEventContext();

  const location = React.useRef(event.location);

  const SetCenterComponent = ({ center }: { center: LatLngLiteral }) => {
    const map = useMap();
    React.useEffect(() => {
      if (center !== location.current) {
        location.current = center;
        map.panTo(center);
      }
    }, [center, map]);
    return null;
  };

  const ResizeMap = () => {
    const map = useMap();
    React.useEffect(() => {
      const container = map.getContainer();
      const observer = new ResizeObserver(() => {
        map.invalidateSize();
      });
      observer.observe(container);
      map.invalidateSize();
      return () => observer.disconnect();
    }, [map]);
    return null;
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <LeafletRightClickProvider>
        <MapContainer
          center={event.location}
          zoom={event.zoom}
          style={{
            width: '100%',
            height: '100%',
            zIndex: '0',
            cursor: 'default'
          }}
          maxZoom={22}
        >
          <HideAttributeControl />
          <ResizeMap />
          <SetCenterComponent center={options.center} />
          <TileLayer url="https://tile.thunderforest.com/cycle/{z}/{x}/{y}.png?apikey=8415e76f241042fb85d6e744d874ab6c" />
          <ScaleControl position="bottomright" imperial={false} />
          <ReactLeafletRightClick customComponent={<MapContextMenu />} />
          <LayersControl position="topleft">
            {event.maps &&
              event.maps.map((m) => (
                <LayersControl.Overlay key={`map-${m.url || m.id}`} name={m.name} checked={true}>
                  <LayerGroup>
                    <ImageOverlayRotated
                      topLeft={m.topLeft}
                      topRight={m.topRight}
                      bottomLeft={m.bottomLeft}
                      opacity={1}
                      image={import.meta.env.VITE_UPLOADS + m.url}
                    />
                  </LayerGroup>
                </LayersControl.Overlay>
              ))}
            {event.routes &&
              event.routes.filter((r) => !(options.hiddenRouteIds ?? []).includes(r.id)).map((r) => (
                <LayersControl.Overlay key={r.id} name={r.name} checked={true}>
                  <Polyline
                    positions={r.track}
                    pathOptions={{
                      color: r.color,
                      opacity: options.routeOpacity,
                      weight: options.routeWeight
                    }}
                  />
                </LayersControl.Overlay>
              ))}
            {event.controls &&
              event.controls.map((m) => (
                <LayersControl.Overlay key={m.id} name={m.name} checked={true}>
                  <Marker position={m.position} icon={icons[m.type]}>
                    <Popup>{m.name}</Popup>
                  </Marker>
                </LayersControl.Overlay>
              ))}
          </LayersControl>
          <Players />
        </MapContainer>
      </LeafletRightClickProvider>
      <IconButton
        color="primary"
        size="large"
        aria-label="Open competitors"
        edge="end"
        onClick={() => setOpen(true)}
        sx={{
          position: 'absolute',
          background: '#ffffffaa',
          zIndex: 1100,
          top: 'env(safe-area-inset-top, 0px)',
          right: 0,
          margin: 0,
          minWidth: 44,
          minHeight: 44,
          display: open ? 'none' : 'flex'
        }}
      >
        <MenuIcon />
      </IconButton>
      <RightDrawer loading={loading} open={open} setOpen={setOpen} />
    </Box>
  );
};

export default MapEvent;
